import { useQueryClient } from '@tanstack/react-query';
import { ChartAssetData, EventsConfig, RangeHorizon } from '@toggle/chart';
import { ChartInsight } from '@toggle/toggle';
import { subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { getCatalystInsightListForChart } from '~/services/insights/catalyst-insights-service';
import { getEarningsInsightsForChart } from '~/services/insights/earnings-insights-service';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { ChartSearchParams } from '~/utils/chart-search-utils/chart-search-utils';
import { fetchFeed } from '~/views/turbo-chart/feed-services/feed';
import { deduplicateEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';

const getStartDate = (endDate: Date, chartHorizon?: RangeHorizon) => {
  switch (chartHorizon) {
    case RangeHorizon.OneDay:
      return subDays(endDate, 1);
    case RangeHorizon.OneWeek:
      return subWeeks(endDate, 1);
    case RangeHorizon.TwoWeeks:
      return subWeeks(endDate, 2);
    case RangeHorizon.OneMonth:
      return subMonths(endDate, 1);
    case RangeHorizon.ThreeMonths:
      return subMonths(endDate, 3);
    case RangeHorizon.OneYear:
      return subYears(endDate, 1);
    case RangeHorizon.ThreeYears:
      return subYears(endDate, 3);
    case RangeHorizon.FiveYears:
      return subYears(endDate, 5);
    case RangeHorizon.TenYears:
      return subYears(endDate, 10);
    case RangeHorizon.SixMonths:
    default:
      return subMonths(endDate, 6);
  }
};

export interface UseChartInsightsProps {
  chartData: ChartAssetData[];
  eventsConfig: EventsConfig;
  chartHorizon?: RangeHorizon;
  hasStaleTime?: boolean;
  isSimpleChart?: boolean;
}

export const useChartInsights = ({
  chartData,
  eventsConfig,
  chartHorizon,
  hasStaleTime = true,
}: UseChartInsightsProps) => {
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const canShowCompanyCatalysts = !isBasicOrLightUser;

  const [insights, setInsights] = useState<ChartInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [searchParams] = useSearchParams();
  const assets = searchParams.getAll(ChartSearchParams.Asset).toString();
  const queryClient = useQueryClient();

  const fetchInsights = async (primaryAsset: ChartAssetData) => {
    const ts = primaryAsset.ts;
    const endDate = new Date(ts[ts.length - 1].time);
    const startDate = getStartDate(endDate, chartHorizon);

    const articles = await queryClient.fetchQuery({
      queryKey: ['article-insights', primaryAsset.entity.tag],
      queryFn: () =>
        fetchFeed({
          entityTag: primaryAsset.entity.tag,
          startDate,
          endDate: new Date(ts[ts.length - 1].time),
        }),
      staleTime: hasStaleTime ? 3_600_000 : undefined,
    });

    const catalystInsights = canShowCompanyCatalysts
      ? await queryClient.fetchQuery({
          queryKey: [
            'catalyst-insights',
            primaryAsset.entity.tag,
            startDate.toISOString(),
            endDate.toISOString(),
          ],
          queryFn: () =>
            getCatalystInsightListForChart({
              entities: [primaryAsset.entity.tag],
              categories: ['company'],
              from: startDate.toISOString(),
              to: new Date(ts[ts.length - 1].time).toISOString(),
            }),
          staleTime: hasStaleTime ? 3_600_000 : undefined,
        })
      : { data: [], error: false };

    const earningsInsights = isBasicOrLightUser
      ? { data: [], error: false }
      : await queryClient.fetchQuery({
          queryKey: [
            'earnings-insights',
            primaryAsset.entity.tag,
            startDate.toISOString(),
            endDate.toISOString(),
          ],
          queryFn: () =>
            getEarningsInsightsForChart({
              entities: [primaryAsset.entity.tag],
              from: startDate.toISOString(),
              to: new Date(ts[ts.length - 1].time).toISOString(),
            }),
          staleTime: hasStaleTime ? 5 * 60 * 1000 : undefined,
        });

    if (articles.error || catalystInsights.error || earningsInsights.error) {
      setIsError(true);
      return [];
    }

    const deduplicatedEarningsInsight = deduplicateEarningsInsights(
      earningsInsights.data
    );

    return [
      ...articles.result,
      ...catalystInsights.data,
      ...deduplicatedEarningsInsight,
    ];
  };

  const getInsights = async () => {
    setInsightsLoading(true);
    setIsError(false);

    const uniqueEntities = new Map<string, ChartAssetData>();
    chartData.forEach(asset => {
      const entityTag = asset.entity.tag;

      if (!uniqueEntities.has(entityTag)) {
        uniqueEntities.set(entityTag, asset);
      }
    });

    const result = await Promise.all(
      [...uniqueEntities.values()].map(fetchInsights)
    );

    setInsights(result.flat());
    setInsightsLoading(false);
  };

  useEffect(() => {
    if (!eventsConfig.TMI) {
      return;
    }

    if (chartData.length) {
      getInsights();
    }
  }, [assets, eventsConfig, chartHorizon, chartData]);

  return {
    insights,
    insightsLoading,
    isError,
    getInsights,
  };
};
